import { StringObject } from "../../objects/StringObject";
import { StringModule } from "../StringModule";

interface BatchTask {
  priority: number;
  execute: () => void;
}

interface ModuleBatchContext {
  module: StringModule;
  object: StringObject;
  element: HTMLElement;
  attributes: Record<string, any>;
  globalId: number;
  windowSize: number;
}

export class DOMBatcher {
  private readQueue: BatchTask[] = [];
  private writeQueue: BatchTask[] = [];
  private computeQueue: BatchTask[] = [];
  private isProcessing = false;
  private pendingFrame: number | null = null;
  private rectCache = new WeakMap<HTMLElement, DOMRect>();
  private dimensionCache = new WeakMap<HTMLElement, { width: number; height: number }>();

  public scheduleRead(task: () => void, priority: number = 0): void {
    this.readQueue.push({ priority, execute: task });
    this.scheduleFlush();
  }

  public scheduleCompute(task: () => void, priority: number = 0): void {
    this.computeQueue.push({ priority, execute: task });
    this.scheduleFlush();
  }

  public scheduleWrite(task: () => void, priority: number = 0): void {
    this.writeQueue.push({ priority, execute: task });
    this.scheduleFlush();
  }

  public batchModuleInitialization(contexts: ModuleBatchContext[]): void {
    contexts.forEach(({ module, object, element, attributes, globalId }) => {
      this.scheduleRead(() => {
        const rect = element.getBoundingClientRect();
        this.rectCache.set(element, rect);
        this.dimensionCache.set(element, {
          width: element.offsetWidth || element.clientWidth || rect.width,
          height: element.offsetHeight || element.clientHeight || rect.height,
        });
        module.initializeObject(globalId, object, element, attributes);
      }, 1);
    });

    contexts.forEach(({ module, object, windowSize }) => {
      this.scheduleCompute(() => {
        module.calculatePositions(object, windowSize);
      }, 2);
    });

    contexts.forEach(({ module, object }) => {
      this.scheduleWrite(() => {
        module.connectObject(object);
        module.addObject(object.id, object);
      }, 3);
    });
  }

  public getCachedRect(element: HTMLElement): DOMRect | undefined {
    return this.rectCache.get(element);
  }

  public getCachedDimensions(element: HTMLElement): { width: number; height: number } | undefined {
    return this.dimensionCache.get(element);
  }

  private scheduleFlush(): void {
    if (this.pendingFrame !== null || this.isProcessing) {
      return;
    }
    this.pendingFrame = requestAnimationFrame(() => {
      this.flush();
    });
  }

  private flush(): void {
    this.isProcessing = true;
    this.pendingFrame = null;
    const sortByPriority = (a: BatchTask, b: BatchTask) => b.priority - a.priority;

    try {
      const reads = [...this.readQueue].sort(sortByPriority);
      reads.forEach((task) => {
        try {
          task.execute();
        } catch (error) {
          console.error("[DOMBatcher] Read task error:", error);
        }
      });
      this.readQueue = [];

      const computes = [...this.computeQueue].sort(sortByPriority);
      computes.forEach((task) => {
        try {
          task.execute();
        } catch (error) {
          console.error("[DOMBatcher] Compute task error:", error);
        }
      });
      this.computeQueue = [];

      const writes = [...this.writeQueue].sort(sortByPriority);
      writes.forEach((task) => {
        try {
          task.execute();
        } catch (error) {
          console.error("[DOMBatcher] Write task error:", error);
        }
      });
      this.writeQueue = [];
    } finally {
      this.rectCache = new WeakMap();
      this.dimensionCache = new WeakMap();
      this.isProcessing = false;
    }
  }

  public flushSync(): void {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
    this.flush();
  }

  public clear(): void {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
    this.readQueue = [];
    this.writeQueue = [];
    this.computeQueue = [];
    this.rectCache = new WeakMap();
    this.dimensionCache = new WeakMap();
  }
}
